import { useState, type Ref } from 'react';

import { createExpenseHead, expensesKey, type ExpenseHead } from '@/api/cash';
import { useApiMutation } from '@/api/mutations';
import { Button } from '@/components/PageHeading';
import { Field, controlClass, selectClass } from '@/components/Field';
import { headCodeFor } from '@core/cash/expense';

const addValue = '__add';

/**
 * The expense head on a voucher or a recurring expense (2026-10-23, from a test as a new user).
 *
 * <p>
 * <strong>It starts empty</strong> — "Choose a head" — rather than on the first head in the list,
 * because a clerk who pressed Enter past it filed rent under "Bank charges". And <strong>a missing
 * head can be added here</strong>, by name only (the code is made from it, `headCodeFor`), so a clerk
 * mid-entry never has to cancel the form, losing what was typed, to add one on the Heads card.
 * </p>
 */
export function HeadField({
  heads,
  value,
  onChange,
  disabled = false,
  mayAdd = true,
  selectRef,
}: {
  /** Every head the branch has; retired ones are offered only while already chosen. */
  heads: ExpenseHead[];
  value: number | null;
  onChange: (id: number) => void;
  disabled?: boolean;
  /** Whether this person may add a head (`Expense:Create`) — never offer a write the server refuses. */
  mayAdd?: boolean;
  selectRef?: Ref<HTMLSelectElement>;
}) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');

  const add = useApiMutation({
    mutationFn: (input: { code: string; name: string }) => createExpenseHead(input),
    success: (head) => `${head.name} added.`,
    invalidates: [expensesKey],
    onSuccess: (head) => {
      onChange(head.id);
      setAdding(false);
      setName('');
    },
  });

  const offered = heads.filter((head) => head.isActive || head.id === value);

  return (
    <Field label="Head" required>
      {adding ? (
        <div className="flex gap-1.5">
          <input
            // The clerk just asked to type a name: the box takes the cursor as it appears.
            ref={(element) => element?.focus()}
            type="text"
            placeholder="Licences & fees"
            aria-label="New head's name"
            value={name}
            disabled={add.isPending}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              // Enter adds the head here instead of moving the form on; Esc goes back to the list.
              if (event.key === 'Enter') {
                event.preventDefault();
                event.stopPropagation();

                if (name.trim() !== '') {
                  add.mutate({ code: headCodeFor(name, heads), name: name.trim() });
                }
              } else if (event.key === 'Escape') {
                event.stopPropagation();
                setAdding(false);
              }
            }}
            className={controlClass}
          />
          <Button
            variant="primary"
            loading={add.isPending}
            disabled={name.trim() === '' || add.isPending}
            onClick={() => add.mutate({ code: headCodeFor(name, heads), name: name.trim() })}
          >
            Add
          </Button>
          <Button disabled={add.isPending} onClick={() => setAdding(false)}>
            Cancel
          </Button>
        </div>
      ) : (
        <select
          ref={selectRef}
          disabled={disabled}
          value={value ?? ''}
          onChange={(event) => {
            if (event.target.value === addValue) {
              setAdding(true);
            } else {
              onChange(Number(event.target.value));
            }
          }}
          className={selectClass}
        >
          {value === null && <option value="">Choose a head</option>}
          {offered.map((head) => (
            <option key={head.id} value={head.id}>
              {head.name}
              {head.isActive ? '' : ' (retired)'}
            </option>
          ))}
          {mayAdd && <option value={addValue}>＋ Add a head…</option>}
        </select>
      )}
    </Field>
  );
}
